import { FC, MouseEventHandler, PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  RIGHTBAR_DEFAULT_WIDTH,
  RydeUiUnit,
  TDrawerCloseFunction,
  useIsMobile,
} from "@ryde/shared";

import {
  BaseDrawer,
  useDrawerWidthResize,
  type IDrawerWidthResizeConfig,
} from "../drawers";
import { LayoutRightFooter } from "./layout.right.footer";
import { LayoutRightHeader } from "./layout.right.header";
import {
  LayoutRightContentStyled,
  LayoutRightResizeHandleStyled,
} from "./layout.right.styled";

interface ILayoutRight extends PropsWithChildren {
  headerTitle?: string;
  subHeaderTitle?: string;
  headerSubTitle?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  deleteMessage?: string;
  duplicationMessage?: string;
  loading?: boolean;
  closeClick?: MouseEventHandler<HTMLElement>;
  cancelClick?: MouseEventHandler<HTMLElement>;
  saveClick?: MouseEventHandler<HTMLElement>;
  isSaveDisabled?: boolean;
  isInnerItemEditing?: boolean;
  archiveClick?: MouseEventHandler<HTMLElement>;
  isArchiveDisabled?: boolean;
  duplicateClick?: MouseEventHandler<HTMLElement>;
  isDuplicateDisabled?: boolean;
  bgPng?: string;
  customWidth?: string;
  /** Makes the drawer width draggable from its leading edge (desktop only). */
  resizeConfig?: IDrawerWidthResizeConfig;
  showBackgroundImage?: boolean;
  entity?: RydeUiUnit;
  saveLabel?: string;
  cancelLabel?: string;
  positionFixed?: boolean;
  customFooterButtons?: ReactNode;
  headerRightContent?: ReactNode;
  contentNoScroll?: boolean;
}

export const LayoutRight: FC<ILayoutRight> = ({
  headerTitle,
  subHeaderTitle,
  deleteMessage,
  duplicationMessage,
  closeClick,
  cancelClick,
  saveClick,
  isSaveDisabled = false,
  isInnerItemEditing = false,
  archiveClick,
  isArchiveDisabled = false,
  duplicateClick,
  isDuplicateDisabled = false,
  loading,
  showFooter,
  showHeader = true,
  bgPng,
  children,
  customWidth = RIGHTBAR_DEFAULT_WIDTH,
  resizeConfig,
  showBackgroundImage = true,
  entity = "unknown",
  saveLabel,
  cancelLabel,
  customFooterButtons,
  headerRightContent,
  contentNoScroll = false,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { drawerWidthStyle, onResizeHandleMouseDown, onResizeHandleKeyDown } =
    useDrawerWidthResize(resizeConfig);
  const isResizable = !!resizeConfig && !isMobile;

  const onCloseDrawerClick: TDrawerCloseFunction = e => {
    closeClick?.(e as Parameters<MouseEventHandler<HTMLElement>>[0]);
  };

  return (
    <BaseDrawer
      onCloseDrawer={onCloseDrawerClick}
      customWidth={isResizable ? drawerWidthStyle : customWidth}
    >
      {isResizable && (
        <LayoutRightResizeHandleStyled
          onMouseDown={onResizeHandleMouseDown}
          onKeyDown={onResizeHandleKeyDown}
          role="separator"
          aria-orientation="vertical"
          aria-label={t("general.resizeDrawer")}
          tabIndex={0}
        />
      )}
      {showHeader && (
        <LayoutRightHeader
          headerTitle={headerTitle}
          subHeaderTitle={subHeaderTitle}
          closeClick={closeClick}
          headerRightContent={headerRightContent}
        />
      )}
      <LayoutRightContentStyled
        $showFooter={showFooter}
        $showHeader={showHeader}
        bgPng={bgPng}
        $showBackgroundImage={isMobile ? false : showBackgroundImage}
        $isMobile={isMobile}
        $contentNoScroll={contentNoScroll}
      >
        {children}
      </LayoutRightContentStyled>

      {showFooter && (
        <LayoutRightFooter
          saveClick={saveClick}
          isSaveDisabled={isSaveDisabled}
          isInnerItemEditing={isInnerItemEditing}
          closeClick={closeClick}
          cancelClick={cancelClick}
          archiveClick={archiveClick}
          isArchiveDisabled={isArchiveDisabled}
          duplicateClick={duplicateClick}
          isDuplicateDisabled={isDuplicateDisabled}
          deleteMessage={deleteMessage}
          duplicationMessage={duplicationMessage}
          loading={loading}
          entity={entity}
          saveLabel={saveLabel}
          cancelLabel={cancelLabel}
          customButtons={customFooterButtons}
        />
      )}
    </BaseDrawer>
  );
};
